import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';

import BoardCard from './BoardCard';

/**
 * Eén kolom van het bord: een kop met het aantal, de kaarten, en een knop om
 * er meer te tonen.
 *
 * **Waarom er niet alles tegelijk staat.** Bij de grootste organisatie staan
 * 357 van de 394 huishoudens op één status. Een kolom met 357 kaarten is geen
 * bord — je scrolt je suf en de verdeling die je juist wilde zien valt weg. De
 * kop toont daarom altijd het ÉCHTE aantal, niet het aantal getoonde kaarten;
 * anders leest '25' als 'er zijn er 25'.
 *
 * @param {object} props
 * @param {string} props.columnKey - Statussleutel; ook het drop-doel
 * @param {string} props.label - Kolomkop, in de woorden van de organisatie
 * @param {Array<object>} props.items - Alles op deze status
 * @param {number} props.count - Hoeveel het er echt zijn
 * @param {number} props.pageSize - Hoeveel kaarten er per keer bij komen
 * @param {(item: object) => string|number} props.getItemKey - Sleutel per record
 * @param {(item: object) => React.ReactNode} props.renderCard - De inhoud van een kaart
 * @param {boolean} [props.canDrag] - Of er gesleept mag worden
 * @param {string} [props.emptyLabel] - Tekst in een lege kolom
 * @returns {JSX.Element}
 */
const BoardColumn = ({
    columnKey,
    label,
    items,
    count,
    pageSize,
    getItemKey,
    renderCard,
    canDrag,
    emptyLabel = 'Sleep hier een kaart naartoe',
}) => {
    const [zichtbaar, setZichtbaar] = useState(pageSize);
    const { setNodeRef, isOver } = useDroppable({ id: columnKey });

    const getoond = items.slice(0, zichtbaar);
    const rest = count - getoond.length;

    return (
        <section
            className={`board-column${isOver ? ' board-column--over' : ''}`}
            aria-label={`${label} (${count})`}
        >
            <header className="board-column__head">
                <h3 className="board-column__title">{label}</h3>
                <span className="board-column__count">{count}</span>
            </header>

            <div ref={setNodeRef} className="board-column__drop">
                {getoond.map((item) => (
                    <BoardCard
                        key={getItemKey(item)}
                        id={getItemKey(item)}
                        status={columnKey}
                        disabled={!canDrag}
                    >
                        {renderCard(item)}
                    </BoardCard>
                ))}

                {/* Ook een lege kolom moet een kaart kunnen aannemen, dus het
                    drop-vlak blijft bestaan; de tekst zegt alleen wat je hier
                    kunt doen. */}
                {count === 0 && <p className="board-column__empty">{emptyLabel}</p>}
            </div>

            {rest > 0 && (
                <button
                    type="button"
                    className="board-column__more"
                    onClick={() => setZichtbaar((n) => n + pageSize)}
                >
                    {`Toon ${Math.min(rest, pageSize)} meer`}
                </button>
            )}
        </section>
    );
};

export default BoardColumn;
